'use client';

import iconHot from '@/assets/images/icons/icon-hot.svg';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import RoundTextIcon from '@/components/ui/RoundTextIcon/RoundTextIcon';
import { anton } from '@/lib/font';
import { useGlobalLoadingAtom } from '@/state';
import { ImageType, type Product, type Spec } from '@/types';
import { formatCurrency } from '@/utils/formatters';
import { getImageLayoutSizes } from '@/utils/nextImage';
import { getDisplayDataFromProduct } from '@/utils/product';
import { useRouter } from 'next/navigation';
import styles from './ProductCardList.module.scss';

function ProductCardList({ products }: { products: Product[] }) {
  const router = useRouter();
  const [_, startGlobalLoading] = useGlobalLoadingAtom();

  const handleProductClick = (productId: number) => {
    startGlobalLoading();
    router.push(`/product/${productId}/detail`);
  };

  const getSizeText = (sizes: Product['sizes']) => {
    if (sizes.length === 0) {
      return 'FREE';
    }

    return sizes.map((size) => size.name).join(', ');
  };

  const getSpecText = (specs: Spec[]) => {
    if (specs.length === 0) {
      return '';
    }

    const spec = specs.find((s) => s.key === '素材');
    return spec ? `${spec.key}：${spec.value}` : '';
  };

  if (products.length === 0) {
    return <p className={styles.emptyMessage}>アイテムがありません</p>;
  }

  /**
   * 商品バッジをレンダリングする
   * @param product 商品
   * @returns 商品バッジ
   */
  const renderProductBadge = (product: Product) => {
    if (product.status.isHot) {
      return (
        <div className={styles.hotBadgeContainer}>
          <img src={iconHot.src} alt='HOT' className={styles.hotIcon} />
          <span className={`${styles.hotText} ${anton.className}`}>HOT</span>
        </div>
      );
    }

    if (product.status.isNew) {
      return (
        <div className={styles.newBadgeContainer}>
          <RoundTextIcon bgColor='red' text='NEW' size='defaultFixed' />
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles.productCardList}>
      {products.map((product, index) => {
        // 表示する画像（latestUserDesignImageUrlがあればそれ、なければimageUrls.main）
        const displayImageUrl = product.imageUrls.main;

        const displayProductData = getDisplayDataFromProduct(product, index, ImageType.main);

        const handleClick = () => {
          handleProductClick(product.id);
        };

        return (
          <div key={product.id} className={styles.productCard} onClick={handleClick}>
            <div className={styles.topSection}>
              <div className={styles.imageContainer}>
                <NextImageView
                  src={displayImageUrl}
                  alt={product.name}
                  fill
                  objectFit='contain'
                  sizes={getImageLayoutSizes({ sp: '50vw', pc: '200px' })}
                  priority={index < 3}
                />
              </div>
              <div className={styles.productInfo}>
                <p className={styles.specText}>{getSpecText(product.specs)}</p>
                <p className={styles.sizeText}>サイズ：{getSizeText(product.sizes)}</p>
                {product.colors && product.colors.length > 1 && (
                  <div className={styles.colorList}>
                    {product.colors.map((color) => {
                      // 白の場合のみ背景と同化して見づらくなるため、白の場合は枠線を追加
                      const isWhite = /^#?(fff|ffffff)$/i.test(color.colorCode);
                      return (
                        <div
                          key={color.id}
                          className={`${styles.colorItem} ${isWhite ? styles.colorItemWhite : ''}`}
                          style={{ backgroundColor: color.colorCode }}
                          title={color.name}
                        />
                      );
                    })}
                  </div>
                )}
                <p className={styles.priceText}>
                  <span className={styles.priceTextCurrency}>¥</span>
                  {formatCurrency(product.minimumPrice)}〜<span className={styles.priceTextCurrency}>＋送料</span>
                </p>
              </div>
            </div>
            <div className={styles.bottomSection}>
              <div className={styles.productNameContainer}>
                <div className={`${styles.categoryIconContainer} ${styles[`productColor${displayProductData.color}`]}`}>
                  <NextImageView
                    src={displayProductData.iconSrc}
                    alt={displayProductData.alt}
                    fill
                    objectFit='contain'
                    sizes={getImageLayoutSizes({ sp: '40px', pc: '40px' })}
                  />
                </div>
                <div className={styles.productNameWrapper}>
                  <p className={styles.productName}>{product.name}</p>
                </div>
              </div>
            </div>
            {renderProductBadge(product)}
          </div>
        );
      })}
    </div>
  );
}

export default ProductCardList;
